import React, { useContext, useEffect, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	FASTSHIFT_RUNBOOK_STEP_STATUS,
	FastshiftRunbookStep,
	FastshiftRunbookStepStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { FormattedMessage, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import Flag from '@/contexts/FlagProvider';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import {
	updateFastshiftRunbookStepDisabled,
	updateFastshiftRunbookStepRisk,
	updateFastshiftRunbookStepStatus,
} from '@/state/actions';
import { getRunbookStepActionKey } from '@/state/utils';

import { useRunbooksAnalytics } from '../../hooks/use-runbooks-analytics';
import {
	RUNBOOK_STEP_ACTION_TYPE,
	type RunbookStepActionType as RunbookStepAnalyticsActionType,
} from '../../types';
import { messages } from '../messages';

import { RunbookStepCompleteConfirmModal } from './runbook-step-complete-confirm-modal';
import { RunbookStepDisableConfirmModal } from './runbook-step-disable-confirm-modal';
import {
	groupRunbookStepsByPhase,
	isRunbookStepDone,
	type RunbookStepPhaseGroup,
} from './runbook-step-list-utils';
import { RunbookStepNoteModal } from './runbook-step-note-modal';
import { RunbookStepRow } from './runbook-step-row';

const phaseGroupStyles = xcss({
	borderInlineStart: `2px solid ${token('color.border')}`,
	paddingBlockStart: 'space.100',
	paddingInlineStart: 'space.150',
});

const phaseHeaderStyles = xcss({
	borderBlockEnd: `1px solid ${token('color.border')}`,
	paddingBlockEnd: 'space.075',
	paddingInline: 'space.100',
});

type RunbookStepActionKey = 'disabled' | 'risk' | 'status';

const getRunbookStepActionErrorFlagId = (stepId: string, actionType: RunbookStepActionKey) =>
	`runbook-step-action-error-${stepId}-${actionType}`;

const ANALYTICS_ACTION_TYPE_BY_ACTION: Record<
	RunbookStepActionKey,
	RunbookStepAnalyticsActionType
> = {
	disabled: RUNBOOK_STEP_ACTION_TYPE.DISABLE,
	risk: RUNBOOK_STEP_ACTION_TYPE.RISK,
	status: RUNBOOK_STEP_ACTION_TYPE.STATUS,
};

type RunbookPhaseGroupProps = {
	group: RunbookStepPhaseGroup;
	runbookId: string;
	getIsStepActionPending: (stepId: string) => boolean;
	onMenuOpen: (step: FastshiftRunbookStep) => void;
	onToggleDisabled: (step: FastshiftRunbookStep) => void;
	onToggleRisk: (step: FastshiftRunbookStep) => void;
	onToggleStatus: (step: FastshiftRunbookStep) => void;
};

const RunbookPhaseGroup: React.FC<RunbookPhaseGroupProps> = ({
	group,
	runbookId,
	getIsStepActionPending,
	onMenuOpen,
	onToggleDisabled,
	onToggleRisk,
	onToggleStatus,
}) => (
	<Stack space="space.050" xcss={phaseGroupStyles}>
		<Box xcss={phaseHeaderStyles}>
			<Text size="small" weight="semibold">
				{group.phase}
			</Text>
		</Box>
		<Stack space="space.100">
			{group.steps.map((step, index) => (
				<RunbookStepRow
					key={step.stepId}
					step={step}
					hasDivider={index > 0}
					runbookId={runbookId}
					isPending={getIsStepActionPending(step.stepId)}
					onMenuOpen={onMenuOpen}
					onToggleDisabled={onToggleDisabled}
					onToggleRisk={onToggleRisk}
					onToggleStatus={onToggleStatus}
				/>
			))}
		</Stack>
	</Stack>
);

export type RunbookStepListProps = {
	steps: FastshiftRunbookStep[];
	supportTicketId: string;
	runbookId: string;
};

export const RunbookStepList: React.FC<RunbookStepListProps> = ({
	steps,
	supportTicketId,
	runbookId,
}) => {
	const intl = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const { showFlag } = useContext(Flag.Context);
	const {
		sendRunbookStepActionsMenuUIEvent,
		sendRunbookStepStatusToggleUIEvent,
		sendRunbookStepRiskToggleUIEvent,
		sendRunbookStepDisableToggleUIEvent,
		sendRunbookStepActionErrorFlagTrackEvent,
		sendRunbookStepStatusConfirmModalTrackEvent,
		sendRunbookStepStatusConfirmButtonClickUIEvent,
		sendRunbookStepStatusConfirmCancelButtonClickUIEvent,
		sendRunbookStepDisableConfirmModalTrackEvent,
		sendRunbookStepDisableConfirmButtonClickUIEvent,
		sendRunbookStepDisableCancelButtonClickUIEvent,
	} = useRunbooksAnalytics();

	const getStepContext = (step: FastshiftRunbookStep) => ({
		supportTicketId,
		runbookId,
		stepId: step.stepId,
		stepOrder: step.stepOrder,
		phase: step.phase,
	});

	const [noteModalStep, setNoteModalStep] = useState<FastshiftRunbookStep | null>(null);
	const [completeModalStep, setCompleteModalStep] = useState<FastshiftRunbookStep | null>(null);
	const [disableModalStep, setDisableModalStep] = useState<FastshiftRunbookStep | null>(null);

	const groupedSteps = groupRunbookStepsByPhase(
		steps,
		intl.formatMessage(messages.runbooksPanelOtherPhaseLabel),
	);

	const stepActionsByStepKey = useFastshiftSelector(
		(state) => state.runbooks.stepActionsByStepKey ?? {},
	);

	const getIsStepActionPending = (stepId: string) => {
		return stepActionsByStepKey[getRunbookStepActionKey({ runbookId, stepId })]?.pending ?? false;
	};

	const showActionErrorFlag = (
		stepId: string,
		actionType: RunbookStepActionKey,
		retryAction: () => void,
	) => {
		sendRunbookStepActionErrorFlagTrackEvent({
			supportTicketId,
			runbookId,
			stepId,
			actionType: ANALYTICS_ACTION_TYPE_BY_ACTION[actionType],
		});

		showFlag({
			id: getRunbookStepActionErrorFlagId(stepId, actionType),
			iconType: 'error',
			styleOverride: true,
			title: intl.formatMessage(messages.runbooksPanelStepActionErrorFlagTitle),
			description: intl.formatMessage(messages.runbooksPanelStepActionErrorFlagDescription),
			actions: [
				{
					content: intl.formatMessage(messages.runbooksPanelStepActionRetryAction),
					onClick: retryAction,
				},
			],
		});
	};

	const runDisabledToggle = (step: FastshiftRunbookStep) => {
		const retryAction = () => runDisabledToggle(step);

		return dispatch(
			updateFastshiftRunbookStepDisabled({
				stargateClient,
				supportTicketId,
				runbookId,
				stepId: step.stepId,
				disabled: !step.disabled,
			}),
		)
			.unwrap()
			.catch((error) => {
				showActionErrorFlag(step.stepId, 'disabled', retryAction);
				return Promise.reject(error);
			});
	};

	const runStatusUpdate = (
		step: FastshiftRunbookStep,
		status: FastshiftRunbookStepStatus,
		note?: string,
	) => {
		const retryAction = () => runStatusUpdate(step, status, note);

		return dispatch(
			updateFastshiftRunbookStepStatus({
				stargateClient,
				supportTicketId,
				runbookId,
				stepId: step.stepId,
				status,
				note,
				// For Marathon analytics attribution.
				title: step.title,
				phase: step.phase,
				responsible: step.responsible,
			}),
		)
			.unwrap()
			.catch((error) => {
				showActionErrorFlag(step.stepId, 'status', retryAction);
				return Promise.reject(error);
			});
	};

	const handleMenuOpen = (step: FastshiftRunbookStep) => {
		sendRunbookStepActionsMenuUIEvent({
			...getStepContext(step),
			disabled: step.disabled,
			risk: step.risk,
			status: step.status,
		});
	};

	const handleToggleDisabled = (step: FastshiftRunbookStep) => {
		const targetDisabled = !step.disabled;

		sendRunbookStepDisableToggleUIEvent({
			...getStepContext(step),
			targetDisabled,
		});
		setDisableModalStep(step);
	};

	const handleConfirmDisabled = (step: FastshiftRunbookStep) => {
		sendRunbookStepDisableConfirmButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
			targetDisabled: !step.disabled,
		});
		runDisabledToggle(step)
			.then(() => setDisableModalStep(null))
			.catch(() => {});
	};

	const handleCancelDisabled = (step: FastshiftRunbookStep) => {
		sendRunbookStepDisableCancelButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
		});
		setDisableModalStep(null);
	};

	const handleToggleRisk = (step: FastshiftRunbookStep) => {
		const retryAction = () => handleToggleRisk(step);

		sendRunbookStepRiskToggleUIEvent({
			...getStepContext(step),
			targetRisk: !step.risk,
		});

		dispatch(
			updateFastshiftRunbookStepRisk({
				stargateClient,
				supportTicketId,
				runbookId,
				stepId: step.stepId,
				risk: !step.risk,
			}),
		)
			.unwrap()
			.catch(() => showActionErrorFlag(step.stepId, 'risk', retryAction));
	};

	const handleToggleStatus = (step: FastshiftRunbookStep) => {
		const isDone = isRunbookStepDone(step);

		sendRunbookStepStatusToggleUIEvent({
			...getStepContext(step),
			targetStatus: isDone
				? FASTSHIFT_RUNBOOK_STEP_STATUS.NOT_DONE
				: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: !isDone && Boolean(step.requiresComment),
		});

		if (isDone) {
			runStatusUpdate(step, FASTSHIFT_RUNBOOK_STEP_STATUS.NOT_DONE).catch(() => {});
			return;
		}

		if (step.requiresComment) {
			setNoteModalStep(step);
			return;
		}

		setCompleteModalStep(step);
	};

	const handleConfirmNote = (step: FastshiftRunbookStep, noteAdf?: string) => {
		// Note: never emit the note text or any user-entered note content.
		sendRunbookStepStatusConfirmButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: true,
		});
		runStatusUpdate(step, FASTSHIFT_RUNBOOK_STEP_STATUS.DONE, noteAdf)
			.then(() => setNoteModalStep(null))
			.catch(() => {});
	};

	const handleCancelNote = (step: FastshiftRunbookStep) => {
		sendRunbookStepStatusConfirmCancelButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: true,
		});
		setNoteModalStep(null);
	};

	const handleConfirmComplete = (step: FastshiftRunbookStep) => {
		sendRunbookStepStatusConfirmButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: false,
		});
		runStatusUpdate(step, FASTSHIFT_RUNBOOK_STEP_STATUS.DONE)
			.then(() => setCompleteModalStep(null))
			.catch(() => {});
	};

	const handleCancelComplete = (step: FastshiftRunbookStep) => {
		sendRunbookStepStatusConfirmCancelButtonClickUIEvent({
			supportTicketId,
			runbookId,
			stepId: step.stepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: false,
		});
		setCompleteModalStep(null);
	};

	const noteModalStepId = noteModalStep?.stepId;
	useEffect(() => {
		if (!noteModalStepId) {
			return;
		}

		sendRunbookStepStatusConfirmModalTrackEvent({
			supportTicketId,
			runbookId,
			stepId: noteModalStepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: true,
		});
	}, [noteModalStepId, runbookId, sendRunbookStepStatusConfirmModalTrackEvent, supportTicketId]);

	const completeModalStepId = completeModalStep?.stepId;
	useEffect(() => {
		if (!completeModalStepId) {
			return;
		}

		sendRunbookStepStatusConfirmModalTrackEvent({
			supportTicketId,
			runbookId,
			stepId: completeModalStepId,
			targetStatus: FASTSHIFT_RUNBOOK_STEP_STATUS.DONE,
			isNoteRequired: false,
		});
	}, [
		completeModalStepId,
		runbookId,
		sendRunbookStepStatusConfirmModalTrackEvent,
		supportTicketId,
	]);

	const disableModalStepId = disableModalStep?.stepId;
	const targetDisabled = disableModalStep ? !disableModalStep.disabled : undefined;
	useEffect(() => {
		if (!disableModalStepId || targetDisabled === undefined) {
			return;
		}

		sendRunbookStepDisableConfirmModalTrackEvent({
			supportTicketId,
			runbookId,
			stepId: disableModalStepId,
			targetDisabled,
		});
	}, [
		disableModalStepId,
		runbookId,
		sendRunbookStepDisableConfirmModalTrackEvent,
		supportTicketId,
		targetDisabled,
	]);

	return (
		<>
			<Stack space="space.100">
				<Heading size="xsmall" as="h3">
					<FormattedMessage {...messages.runbooksPanelStepsTitle} />
				</Heading>
				<Stack space="space.200">
					{groupedSteps.map((group) => (
						<RunbookPhaseGroup
							key={group.phase}
							group={group}
							runbookId={runbookId}
							getIsStepActionPending={getIsStepActionPending}
							onMenuOpen={handleMenuOpen}
							onToggleDisabled={handleToggleDisabled}
							onToggleRisk={handleToggleRisk}
							onToggleStatus={handleToggleStatus}
						/>
					))}
				</Stack>
			</Stack>
			<RunbookStepNoteModal
				step={noteModalStep}
				isLoading={noteModalStep ? getIsStepActionPending(noteModalStep.stepId) : false}
				onClose={() => noteModalStep && handleCancelNote(noteModalStep)}
				onConfirm={handleConfirmNote}
			/>
			<RunbookStepCompleteConfirmModal
				step={completeModalStep}
				isLoading={completeModalStep ? getIsStepActionPending(completeModalStep.stepId) : false}
				onClose={() => completeModalStep && handleCancelComplete(completeModalStep)}
				onConfirm={handleConfirmComplete}
			/>
			<RunbookStepDisableConfirmModal
				step={disableModalStep}
				isLoading={disableModalStep ? getIsStepActionPending(disableModalStep.stepId) : false}
				onClose={() => disableModalStep && handleCancelDisabled(disableModalStep)}
				onConfirm={handleConfirmDisabled}
			/>
		</>
	);
};
